import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges, AfterViewInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ClassesEntity } from '../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { NotificationService } from '../../../../../../services/notification.service';
import { Log } from '../../../../../../services/logger.service';
import { ClassUserAccountService } from '../../../../../../services/class-user-account.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../../../../shared/pagination-options';
import {
  VmSubscriptionAddDialogComponent,
  VmSubscriptionAddDialogResult,
} from './vm-subscription-add-dialog.component';

/** A single row in the ACC subscription table. */
export interface AccSubRow {
  subscriptionId: string;
  displayName: string;
  /** True while a remove request is in flight. */
  isRemoving?: boolean;
  /** True when this subscription is claimed for the class-template AAD user. */
  isClaimed?: boolean;
  /** True when a participant User AAD account is provisioned on this subscription. */
  hasUserAad?: boolean;
}

@Component({
  selector: 'app-account-subscription-settings',
  templateUrl: './account-subscription-settings.component.html',
  styleUrls: ['./account-subscription-settings.component.css'],
})
export class AccountSubscriptionSettingsComponent implements OnChanges, AfterViewInit {
  @Input() classId!: string;
  @Input() classEntity: ClassesEntity | null = null;
  /**
   * Number of enrolled participants — used to compute the low-subs warning banner.
   * The formula is: at least `participantCount + 1` subscriptions recommended
   * (one reserved for the class-template AAD user).
   * Computed from classEntity when not explicitly passed.
   */
  @Input() participantCount = 0;

  /** Emitted when subscriptions are added or removed so the parent can re-fetch classEntity. */
  @Output() subscriptionsChanged = new EventEmitter<void>();
  /** Emitted when the user clicks the deep-link in the tenant-gate banner. */
  @Output() goToTenant = new EventEmitter<void>();

  dataSource = new MatTableDataSource<AccSubRow>([]);
  displayedColumns = ['displayName', 'subscriptionId', 'actions'];
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  private readonly logger = Log.create(AccountSubscriptionSettingsComponent);

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly notification: NotificationService,
    private readonly dialog: MatDialog,
    private readonly uaService: ClassUserAccountService,
  ) { }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnChanges(_: SimpleChanges): void {
    this.rebuildRows();
    this.loadUserAadPins();
  }

  /** Convenience accessor for the current row list. */
  get rows(): AccSubRow[] {
    return this.dataSource.data;
  }

  /** Subscription IDs (lowercased) with a non-`NotStarted` participant User AAD account pinned. */
  private userAadPinnedSubIds = new Set<string>();

  /**
   * Loads participant User AAD records (`class_user_accounts`) so rows with a dependent
   * account can be flagged and their delete button proactively disabled — mirrors the
   * backend guard added in `RemoveClassAccSubscription`. Best-effort: on failure the button
   * simply falls back to relying on the backend's authoritative guard + error.
   */
  private loadUserAadPins(): void {
    if (!this.classId) return;
    this.uaService.getAll(this.classId).subscribe({
      next: (records) => {
        this.userAadPinnedSubIds = new Set(
          (records ?? [])
            .filter(r => !!r.subscriptionId && r.status !== 'NOT_STARTED' && (r.status as string) !== 'NotStarted')
            .map(r => (r.subscriptionId as string).toLowerCase())
        );
        this.rebuildRows();
      },
      error: () => { /* silent — proactive disable is best-effort; backend guard remains authoritative */ },
    });
  }

  private rebuildRows(): void {
    const raw: any[] = this.classEntity?.data?.['AccSubscription'] ?? [];
    const claimedId = this.claimedSubscriptionId;
    this.dataSource.data = raw.map(r => {
      const subId = (r.subscriptionId ?? r.SubscriptionId ?? '');
      return {
        subscriptionId: subId,
        displayName: r.displayName ?? r.DisplayName ?? '',
        isClaimed: subId === claimedId,
        hasUserAad: this.userAadPinnedSubIds.has(subId.toLowerCase()),
      };
    });
  }

  /**
   * The subscription ID that has been atomically claimed for the class-template AAD user.
   * Removal of this subscription is blocked while the template AAD user is active.
   */
  get claimedSubscriptionId(): string {
    const claim = this.classEntity?.data?.['AccTemplateSubscriptionClaim'] as any;
    return claim?.subscriptionId ?? claim?.SubscriptionId ?? '';
  }

  /**
   * Effective participant count — falls back to computing from the classEntity roster
   * when the parent did not provide a value via `@Input() participantCount`.
   */
  get effectiveParticipantCount(): number {
    if (this.participantCount > 0) return this.participantCount;
    const roster: any[] = (this.classEntity as any)?.roster ?? [];
    return roster.length;
  }

  /**
   * True when the number of subscriptions is below the recommended minimum
   * (participantCount + 1, where 1 is reserved for the class-template AAD user).
   * Shown as a warning banner — does not block adding participants.
   */
  get isLowOnSubscriptions(): boolean {
    return this.rows.length < this.effectiveParticipantCount + 1;
  }

  get lowSubsWarning(): string {
    const need = this.effectiveParticipantCount + 1;
    return `Only ${this.rows.length} subscription(s) added — at least ${need} recommended ` +
      `(${this.effectiveParticipantCount} participant(s) + 1 for template testing).`;
  }

  /** True when the class has Azure SP credentials stored (Data.IsSpConfigured === true). */
  get isTenantConfigured(): boolean {
    return !!this.classEntity?.data?.['IsSpConfigured'];
  }

  /**
   * Opens the shared subscription-add dialog in ACC mode.
   * The dialog handles multi-ID paste, sequential adds, and results display.
   */
  openAddDialog(): void {
    const ref = this.dialog.open(VmSubscriptionAddDialogComponent, {
      data: {
        classId: this.classId,
        existingSubIds: this.rows.map(r => r.subscriptionId),
        mode: 'acc' as const,
      },
      disableClose: false,
      width: '580px',
    });

    ref.afterClosed().subscribe((result: VmSubscriptionAddDialogResult | null) => {
      if (result?.added?.length) {
        this.subscriptionsChanged.emit();
      }
    });
  }

  removeRow(row: AccSubRow): void {
    if (row.isRemoving || row.isClaimed || row.hasUserAad) return;
    row.isRemoving = true;
    this.classService.removeClassAccSubscription(this.classId, row.subscriptionId).subscribe({
      next: () => {
        row.isRemoving = false;
        this.notification.success('Subscription removed.');
        this.subscriptionsChanged.emit();
      },
      error: (err: any) => {
        row.isRemoving = false;
        const msg = err?.error?.message ?? 'Failed to remove subscription.';
        this.notification.error(msg);
        this.logger.e('Remove ACC subscription error:', err);
      },
    });
  }

  removeTooltip(row: AccSubRow): string {
    if (row.isClaimed) {
      return 'Cannot remove — this subscription is in use by the class-template AAD user.';
    }
    if (row.hasUserAad) {
      return 'Cannot remove — a participant User AAD account is provisioned on this subscription.';
    }
    return 'Remove this subscription';
  }
}
